import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useUserOrganization } from '@/hooks/useUserOrganization'
import { useDashboardStore } from '../stores/dashboardStore'
import { useDashboardFilterArgs } from './useDashboardFilterArgs'
import { splitPage } from '../utils/pagination'
import type { DashboardTableOrder } from '../types/dashboard.types'

/**
 * Ordretabellen. Ett steg, ingen aggregat over treffmengden (#934):
 * kostnaden er bundet av sidestørrelsen uansett hvor mange ordrer som finnes.
 *
 * RPC-en henter `p_limit + 1` rader. Den ekstra raden vises aldri — den er
 * signalet «det finnes en side til», og erstatter `COUNT(*)` som drev de
 * gamle sidetallene. Antall og sum: se `useDashboardTableTotals`.
 */
export function useDashboardTable() {
  const { data: membership } = useUserOrganization()
  const orgId = membership?.organization_id

  const pageIndex = useDashboardStore((s) => s.pageIndex)
  const pageSize = useDashboardStore((s) => s.pageSize)
  const sortColumn = useDashboardStore((s) => s.sortColumn)
  const sortDirection = useDashboardStore((s) => s.sortDirection)
  const filterArgs = useDashboardFilterArgs(orgId)

  return useQuery({
    queryKey: ['dashboard-table-orders', filterArgs, sortColumn, sortDirection, pageIndex, pageSize],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('delivery')
        .rpc('dashboard_table_orders', {
          ...filterArgs,
          p_sort_column: sortColumn,
          p_sort_direction: sortDirection,
          p_limit: pageSize,
          p_offset: pageIndex * pageSize,
        })

      if (error) {
        console.error('Dashboard table error:', error)
        throw error
      }

      return splitPage((data || []) as DashboardTableOrder[], pageSize)
    },
    enabled: !!orgId,
    retry: 1,
    staleTime: 30_000,
  })
}
